import type { ReactNode } from 'react'
import useMediaQuery from '@cannect/hooks/useMediaQuery'
import { Button } from '@cannect/new-components/atoms'
import { TooltipText } from '@cannect/new-components/atoms/Tooltip'
import { MOBILE_WIDTH } from '@cannect/utils/constants'
import { getBlockedReason, type SubscriptionAction } from '@cannect/services/resources/recurrency/helpers'
import type { SubscriptionDetail } from '@cannect/services/resources/recurrency/types'
import { cn } from '@cannect/lib/utils'
import { subscriptionsCopy } from '../copy'

export type ActionDialog = 'skip' | 'postpone' | 'pause' | 'cancel'

type ActionsBarProps = {
  detail: SubscriptionDetail
  onOpen: (dialog: ActionDialog) => void
  onResume: () => void
  resumePending?: boolean
}

const { buttons } = subscriptionsCopy

// Bloqueado com `aria-disabled` (não `disabled`): o botão continua focável,
// mostra o motivo no tooltip e o leitor de tela lê o motivo no nome.
const ActionButton = ({
  reason,
  children,
  onClick,
  variant = 'outline_light',
  className,
  isLoading
}: {
  reason: string | null
  children: ReactNode
  onClick: () => void
  variant?: 'outline_light' | 'default'
  className?: string
  isLoading?: boolean
}) => {
  if (!reason) {
    return (
      <Button variant={variant} size="md" onClick={onClick} className={className} isLoading={isLoading}>
        {children}
      </Button>
    )
  }
  return (
    <TooltipText content={reason}>
      <Button
        variant={variant}
        size="md"
        aria-disabled="true"
        onClick={(event) => event.preventDefault()}
        className={cn('cursor-not-allowed opacity-50 hover:opacity-50', className)}>
        {children}
        <span className="sr-only">: {reason}</span>
      </Button>
    </TooltipText>
  )
}

export const ActionsBar = ({ detail, onOpen, onResume, resumePending }: ActionsBarProps) => {
  const isMobile = useMediaQuery(MOBILE_WIDTH)
  if (detail.status !== 'active' && detail.status !== 'paused') return null

  const reason = (action: SubscriptionAction) => getBlockedReason(detail, action)
  const actions: SubscriptionAction[] =
    detail.status === 'active' ? ['skip', 'postpone', 'pause', 'cancel'] : ['resume', 'cancel']
  const reasons = Array.from(new Set(actions.map(reason).filter(Boolean))) as string[]

  return (
    <section aria-label="Ações da assinatura" className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {detail.status === 'active' && (
          <>
            <ActionButton reason={reason('skip')} onClick={() => onOpen('skip')}>
              {buttons.skip}
            </ActionButton>
            <ActionButton reason={reason('postpone')} onClick={() => onOpen('postpone')}>
              {buttons.postpone}
            </ActionButton>
            <ActionButton reason={reason('pause')} onClick={() => onOpen('pause')}>
              {buttons.pause}
            </ActionButton>
          </>
        )}
        {detail.status === 'paused' && (
          <ActionButton reason={reason('resume')} onClick={onResume} variant="default" isLoading={resumePending}>
            {buttons.resume}
          </ActionButton>
        )}
        <ActionButton
          reason={reason('cancel')}
          onClick={() => onOpen('cancel')}
          className={cn('border-transparent text-red-700 hover:opacity-80')}>
          {buttons.cancel}
        </ActionButton>
      </div>
      {isMobile && reasons.length > 0 && <p className="text-xs text-neutral-600">{reasons[0]}</p>}
    </section>
  )
}
